import { useRef, useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Search, X, Loader2, Settings } from 'lucide-react'
import Logo from '../Logo'
import PortfolioSelector from '../PortfolioSelector'
import NotificationsDropdown from '../NotificationsDropdown'
import SearchResultsDropdown from '../search/SearchResultsDropdown'
import type { SearchResult } from '../../types/navigation'

interface MobileHeaderProps {
  searchQuery: string
  searchResults: SearchResult[]
  showSearchResults: boolean
  searchLoading: boolean
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onResultClick: (result: SearchResult) => void
  onSearchFocus: () => void
  setSearchQuery: (query: string) => void
  setSearchResults: (results: SearchResult[]) => void
  setShowSearchResults: (show: boolean) => void
}

export default function MobileHeader({
  searchQuery,
  searchResults,
  showSearchResults,
  searchLoading,
  onSearchChange,
  onResultClick,
  onSearchFocus,
  setSearchQuery,
  setSearchResults,
  setShowSearchResults,
}: MobileHeaderProps) {
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const mobileSearchRef = useRef<HTMLDivElement>(null)

  // Close mobile search when clicking outside (only active when search is open)
  useEffect(() => {
    if (!mobileSearchOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(e.target as Node)) {
        setMobileSearchOpen(false)
        setShowSearchResults(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [mobileSearchOpen, setShowSearchResults])

  const handleMobileResultClick = (result: SearchResult) => {
    onResultClick(result)
    setMobileSearchOpen(false)
  }

  return (
    <header ref={mobileSearchRef} className="lg:hidden sticky top-0 z-20 px-4 py-3" style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--border-color)', paddingTop: 'max(12px, env(safe-area-inset-top, 12px))' }}>
      <div className="flex items-center justify-between">
        <NavLink to="/dashboard">
          <Logo size="sm" variant="dark" />
        </NavLink>
        <div className="flex items-center gap-1">
          <PortfolioSelector compact />
          <button
            onClick={() => { setMobileSearchOpen(!mobileSearchOpen); if (mobileSearchOpen) { setSearchQuery(''); setShowSearchResults(false) } }}
            className="p-2 rounded-lg transition active:scale-95"
            style={{ color: mobileSearchOpen ? 'var(--color-accent)' : 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <Search className="w-5 h-5" />
          </button>
          <NotificationsDropdown />
          <NavLink
            to="/settings"
            className="p-2 rounded-lg transition"
            style={{ color: 'var(--text-secondary)' }}
          >
            <Settings className="w-5 h-5" />
          </NavLink>
        </div>
      </div>
      {/* Expandable mobile search bar */}
      {mobileSearchOpen && (
        <div className="mt-3 max-h-[60vh] overflow-y-auto">
          <div
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-color)',
            }}
          >
            {searchLoading ? (
              <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
            ) : (
              <Search className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
            )}
            <input
              type="text"
              value={searchQuery}
              onChange={onSearchChange}
              onFocus={onSearchFocus}
              placeholder="Search properties, issues, tenants..."
              autoFocus
              className="text-sm flex-1 placeholder:text-[var(--text-muted)]"
              style={{
                color: 'var(--text-primary)',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                WebkitAppearance: 'none',
              }}
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setSearchResults([]); setShowSearchResults(false) }}
                className="p-1"
                style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Mobile search results */}
          <SearchResultsDropdown
            results={searchResults}
            showResults={showSearchResults}
            searchQuery={searchQuery}
            searchLoading={searchLoading}
            onResultClick={handleMobileResultClick}
            variant="mobile"
          />
        </div>
      )}
    </header>
  )
}
